import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SITE } from "../mock";
import { Mail, Send, Loader2, Rss, Podcast } from "lucide-react";
import { NewsletterAPI, api } from "../lib/api";
import { toast } from "sonner";

// Transistor.fm is the canonical distribution feed — Apple + Spotify sync
// from this URL, so surface it directly (not the internal /api/podcast.xml).
const RSS_URL = "https://feeds.transistor.fm/vexent-wire";
// Apple + Spotify won't take an arbitrary XML directly, but pointing the user
// at the raw feed URL is the correct thing to give them for one-click "add by
// URL" in any podcast client. Once the show is claimed in Apple Podcasts
// Connect and Spotify for Podcasters, replace the two anchor hrefs below.
const APPLE_URL = `https://podcasts.apple.com/podcast/id?feed=${encodeURIComponent(RSS_URL)}`;
const SPOTIFY_URL = `https://open.spotify.com/search/${encodeURIComponent("Vexent Wire podcast")}`;

const MASTHEAD_LINKS = [
  { label: "About",             href: "/masthead/about" },
  { label: "Editorial Policy",  href: "/masthead/editorial" },
  { label: "Ethics & Disclosure", href: "/masthead/ethics" },
  { label: "Contact",           href: "/masthead/contact" },
];

const LEGAL_LINKS = [
  { label: "Terms of Service",  href: "/legal/terms" },
  { label: "Billing & Refunds", href: "/legal/billing" },
  { label: "Privacy Policy",    href: "/masthead/privacy" },
  { label: "Disclaimer",        href: "/masthead/disclaimer" },
  { label: "Comment Policy",    href: "/legal/comments" },
  { label: "Copyright & DMCA",  href: "/legal/dmca" },
];

const READ_LINKS = [
  { label: "Feed",      href: "/" },
  { label: "The Wire",  href: "/wire" },
  { label: "Archive",   href: "/archive" },
  { label: "Desk",      href: "/desk" },
  { label: "Subscribe", href: "/subscribe" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [podcastEnabled, setPodcastEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/config")
      .then((r) => { if (alive) setPodcastEnabled(!!r?.data?.podcastEnabled); })
      .catch(() => setPodcastEnabled(false));
    return () => { alive = false; };
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await NewsletterAPI.subscribe(email.trim(), "footer");
      toast.success("You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error("Subscribe failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <footer className="bg-[#0B1F33] text-[#D7DEE5] mt-16">
      {/* Top: brand + link columns */}
      <div className="max-w-[1400px] mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <div className="font-serif-display text-3xl font-bold text-[#FFFFFF]">{SITE.name}</div>
          <div className="mt-1 text-[11px] tracking-[0.3em] uppercase text-[#0891B2]">{SITE.tagline}</div>
          <p className="mt-5 text-[13px] text-[#D7DEE5]/80 max-w-md leading-relaxed">
            Vexent Wire publishes independent research, market strategy, and editorial commentary. Editorially independent since 2001.
          </p>
          <p className="mt-3 text-[12px] text-[#D7DEE5]/60 max-w-md leading-relaxed">
            Nothing on this site constitutes investment, legal, tax, or accounting advice. All content is provided for informational and educational purposes only.
          </p>

          <div className="mt-6">
            {podcastEnabled ? (
              <>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-condensed font-bold mb-2">
              Listen on
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={APPLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-[#1f3454] hover:border-[#B8E9F1] px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#D7DEE5] hover:text-[#0891B2] transition-colors"
                data-testid="footer-apple-podcasts"
              >
                <Podcast className="w-3.5 h-3.5" /> Apple
              </a>
              <a
                href={SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-[#1f3454] hover:border-[#B8E9F1] px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#D7DEE5] hover:text-[#0891B2] transition-colors"
                data-testid="footer-spotify"
              >
                <Podcast className="w-3.5 h-3.5" /> Spotify
              </a>
              <a
                href={RSS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-[#1f3454] hover:border-[#B8E9F1] px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#D7DEE5] hover:text-[#0891B2] transition-colors"
                data-testid="footer-rss-feed"
              >
                <Rss className="w-3.5 h-3.5" /> RSS
              </a>
            </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-condensed font-bold mb-3">Read</div>
          <ul className="space-y-2 text-[12px] tracking-[0.05em] text-[#D7DEE5]/80">
            {READ_LINKS.map((l) => (
              <li key={l.href}><Link className="hover:text-[#0891B2] transition-colors" to={l.href}>{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-condensed font-bold mb-3">Masthead</div>
          <ul className="space-y-2 text-[12px] tracking-[0.05em] text-[#D7DEE5]/80">
            {MASTHEAD_LINKS.map((l) => (
              <li key={l.href}><Link className="hover:text-[#0891B2] transition-colors" to={l.href}>{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-condensed font-bold mb-3">Insider Brief</div>
          <p className="text-[12px] text-[#D7DEE5]/80 mb-3 leading-relaxed">
            Weekly research digest, delivered when it's worth reading. Free.
          </p>
          <form onSubmit={submit} className="flex gap-2" data-testid="footer-newsletter">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0891B2]" />
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full rounded-none pl-9 pr-3 py-2 bg-[#132f57]/40 border border-[#1f3454] text-[#D7DEE5] placeholder:text-[#D7DEE5]/40 focus:border-[#B8E9F1] focus:outline-none text-[13px]"
                data-testid="footer-email"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="rounded-none bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] uppercase tracking-wider text-[11px] font-semibold px-4 py-2 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="footer-subscribe-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </form>
        </div>
      </div>

      {/* Legal row */}
      <div className="border-t border-[#1f3454]">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[10px] tracking-[0.2em] uppercase text-[#D7DEE5]/60">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.href} className="hover:text-[#0891B2] transition-colors" to={l.href}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright row */}
      <div className="border-t border-[#1f3454]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-[10px] tracking-[0.2em] uppercase text-[#D7DEE5]/50">
          <div>© 2001–{new Date().getFullYear()} Vexent Wire · All Rights Reserved</div>
          <div>Independent Research · Not Advice · Reader-Directed</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
